// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { DndContext, type DragEndEvent } from "@dnd-kit/core";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Search, UnfoldHorizontal, X } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router";
import { toast } from "sonner";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { ColumnAliasModal } from "@/components/column-alias-modal";
import { AIFieldEditModal } from "@/components/deep-research/ai-field-edit-modal";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import ResetTableMenu from "@/components/reset-table-menu";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useInfiniteAIFields } from "@/hooks/api/use-ai-fields";
import { usePermissions } from "@/hooks/auth/use-permissions";
import { useAIFieldsColumnSelection } from "@/hooks/use-ai-fields-column-selection";
import { useAIFieldsListFilters } from "@/hooks/use-ai-fields-list-filters";
import { useOrganizationSessionStorage } from "@/hooks/use-browser-storage";
import { useDeepResearchEnabled } from "@/hooks/use-feature-flags";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { useSearchState } from "@/hooks/use-search-state";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import type { ColumnAliases } from "@/lib/column-aliases";
import {
  createBulkColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { cn } from "@/lib/utils";
import type { AIFieldDefinition } from "@/types/deep-research-reports";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { getAIFieldColumnDefs, getAIFieldColumns } from "./ai-fields-columns";

export function AIFieldsDataTable() {
  const { isAdmin } = usePermissions();
  const deepResearchEnabled = useDeepResearchEnabled();
  const { state, isMobile } = useSidebar();

  // State
  const [selectedField, setSelectedField] = useState<AIFieldDefinition | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [aliasModalColumn, setAliasModalColumn] = useState<string | null>(null);

  // Search state
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("ai-fields::list");

  // Use the filters hook
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters: clearAllFiltersBase,
    hasFilters,
    getQueryParams,
  } = useAIFieldsListFilters();

  // Column management
  const coreColumns = useMemo(() => getAIFieldColumns(), []);
  const {
    selectedColumns,
    columnOrder,
    setColumnOrder,
    toggleColumn,
    resetColumns,
  } = useAIFieldsColumnSelection(coreColumns);

  // Persisted column state
  const {
    columnSizing,
    setColumnSizing,
    pinnedColumns,
    setPinnedColumns,
    wrappedColumns,
    setWrappedColumns,
  } = usePersistedColumnState("ai-fields::list", {
    getDefaultPinnedColumns: () => ["display_name"],
  });

  // Column aliases
  const [columnAliases, setColumnAliases] =
    useOrganizationSessionStorage<ColumnAliases>(
      "ai-fields::list::columns::aliases",
      {},
    );

  // Table ref for resizing
  const tableRef = useRef<HTMLTableElement>(null);

  // Build query parameters for API call
  const queryParams = useMemo(() => {
    return getQueryParams({
      searchTerm: debouncedSearchQuery,
      columns: selectedColumns,
      limit: 50,
    });
  }, [getQueryParams, debouncedSearchQuery, selectedColumns]);

  // Fetch AI fields with infinite scrolling
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteAIFields(queryParams);

  // Flatten the infinite query data
  const aiFields = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.ai_fields);
  }, [data]);

  // Handlers
  const handleEditField = useCallback((field: AIFieldDefinition) => {
    setSelectedField(field);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedField(null);
    refetch();
  }, [refetch]);

  const handleSort = useCallback(
    (columnId: string) => {
      const currentSortColumn = filters.sortColumn;
      const currentSortDirection = filters.sortDirection;

      if (currentSortColumn === columnId) {
        // Cycle through: asc -> desc -> no sort
        if (currentSortDirection === "asc") {
          updateFilter("sortDirection", "desc");
        } else if (currentSortDirection === "desc") {
          updateFilter("sortColumn", "");
          updateFilter("sortDirection", null);
        }
      } else {
        // Start with ascending sort for new column
        updateFilter("sortColumn", columnId);
        updateFilter("sortDirection", "asc");
      }
    },
    [filters.sortColumn, filters.sortDirection, updateFilter],
  );

  const clearAllFilters = useCallback(() => {
    clearAllFiltersBase();
    clearSearch();
  }, [clearAllFiltersBase, clearSearch]);

  // Column operations
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        setColumnOrder((oldOrder: string[]) => {
          const oldIndex = oldOrder.indexOf(active.id as string);
          const newIndex = oldOrder.indexOf(over.id as string);
          const newOrder = [...oldOrder];
          newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, active.id as string);
          return newOrder;
        });
      }
    },
    [setColumnOrder],
  );

  const togglePin = useCallback(
    (columnId: string) => {
      setPinnedColumns((prev) =>
        prev.includes(columnId)
          ? prev.filter((id) => id !== columnId)
          : [...prev, columnId],
      );
    },
    [setPinnedColumns],
  );

  const toggleWrap = useCallback(
    (columnId: string) => {
      setWrappedColumns((prev) =>
        prev.includes(columnId)
          ? prev.filter((id) => id !== columnId)
          : [...prev, columnId],
      );
    },
    [setWrappedColumns],
  );

  const handleSetAlias = useCallback((columnId: string) => {
    setAliasModalColumn(columnId);
  }, []);

  const handleSaveAlias = useCallback(
    (columnId: string, alias: string) => {
      setColumnAliases((prev) => ({
        ...prev,
        [columnId]: alias,
      }));
      setAliasModalColumn(null);
    },
    [setColumnAliases],
  );

  // Prepare columns for table
  const visibleColumns = useMemo(() => {
    return selectedColumns.filter((col) =>
      columnOrder.includes(col.key as string),
    );
  }, [selectedColumns, columnOrder]);

  // Resize handlers
  const handleSingleColumnResize = useCallback(
    (columnId: string) => {
      createSingleColumnResizeHandler(
        setColumnSizing,
        "ai-fields-table",
      )(columnId);
    },
    [setColumnSizing],
  );

  const handleBulkColumnResize = useCallback(() => {
    const columnIds = visibleColumns.map((col) => col.key as string);
    createBulkColumnResizeHandler(
      setColumnSizing,
      "ai-fields-table",
    )(columnIds);
  }, [setColumnSizing, visibleColumns]);

  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      toggleColumn(columnId, false);
    },
    [toggleColumn],
  );

  // Reset functions
  const handleResetColumns = useCallback(() => {
    resetColumns();
    setColumnSizing({});
    setPinnedColumns([]);
    setWrappedColumns([]);
    setColumnAliases({});
  }, [
    resetColumns,
    setColumnSizing,
    setPinnedColumns,
    setWrappedColumns,
    setColumnAliases,
  ]);

  const handleResetFilters = useCallback(() => {
    clearAllFilters();
    updateFilter("sortColumn", "created_at");
    updateFilter("sortDirection", "desc");
  }, [clearAllFilters, updateFilter]);

  const handleResetAll = useCallback(() => {
    handleResetColumns();
    handleResetFilters();
    toast.success("Table reset to default");
  }, [handleResetColumns, handleResetFilters]);

  const columnDefs = useMemo(
    () => getAIFieldColumnDefs(visibleColumns, handleEditField),
    [visibleColumns, handleEditField],
  );

  // Create table instance
  const table = useReactTable({
    data: aiFields,
    columns: columnDefs,
    state: {
      columnOrder,
      columnSizing,
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
  });

  // Render helpers
  const renderSortIcon = useCallback(
    (column: UnifiedTableColumn<AIFieldDefinition>) => {
      const direction =
        filters.sortColumn === (column.key as string)
          ? filters.sortDirection
          : null;
      return (
        <SortIcon
          direction={direction as SortDirection}
          isActive={direction !== null}
        />
      );
    },
    [filters],
  );

  // Infinite scroll
  const loadMoreRef = useInfiniteScroll({
    hasNextPage: hasNextPage || false,
    isFetchingNextPage,
    fetchNextPage,
    threshold: 0.1,
  });

  // Check permissions - return after all hooks are declared
  if (!deepResearchEnabled || !isAdmin) {
    return <Navigate to="/home" replace />;
  }

  return (
    <>
      <Card className="h-full relative">
        {/* Header */}
        <div className="p-3 flex items-center justify-between border-b">
          {/* Left side - Search */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search AI fields..."
                value={searchInputValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
              />
              {searchInputValue && (
                <button
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          {/* Right side - Toolbar and count */}
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground mr-2">
              {isLoading
                ? "Loading..."
                : `${aiFields.length} field${aiFields.length !== 1 ? "s" : ""}`}
            </div>

            {/* Resize columns */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleBulkColumnResize()}
                  className="h-9 w-9"
                >
                  <UnfoldHorizontal className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Auto-resize all columns</p>
              </TooltipContent>
            </Tooltip>

            {/* Reset menu */}
            <ResetTableMenu
              onResetColumns={handleResetColumns}
              onResetTableFilters={handleResetFilters}
              onResetAll={handleResetAll}
            />
          </div>
        </div>

        {/* Active filters */}
        {(hasFilters || debouncedSearchQuery) && (
          <div className="px-4 py-2 border-b flex-shrink-0">
            <ActiveFiltersDisplay
              filters={{
                // Include search query if present
                ...(debouncedSearchQuery && {
                  search: debouncedSearchQuery,
                }),
                // Filter out sort-related properties from display
                ...Object.fromEntries(
                  Object.entries(filters).filter(
                    ([key]) => key !== "sortColumn" && key !== "sortDirection",
                  ),
                ),
              }}
              columns={[
                // Add search as a special filter column
                ...(debouncedSearchQuery
                  ? [
                      {
                        key: "search",
                        title: "Search",
                        filterType: "text" as const,
                      },
                    ]
                  : []),
                // Map the columns from the AI fields data
                ...visibleColumns.map((col) => ({
                  key: col.key.toString(),
                  title: col.title,
                  filterType: col.filterType,
                  filterOptions: col.filterOptions,
                })),
              ]}
              clearFilter={(key) => {
                if (key === "search") {
                  clearSearch();
                } else {
                  clearFilter(key);
                }
              }}
              clearAllFilters={clearAllFilters}
              clearAllText="Clear all filters"
              filterType="table"
            />
          </div>
        )}

        {/* Table */}
        <div
          className="rounded-md border-0 max-h-[calc(100vh-13rem)] overflow-auto"
          style={{
            maxWidth: isMobile
              ? "calc(100vw - 2rem)"
              : state === "collapsed"
                ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                : "calc(100vw - var(--sidebar-width) - 2rem)",
          }}
        >
          <DndContext
            collisionDetection={constrainedClosestCenter}
            modifiers={[restrictToTableBounds]}
            onDragEnd={handleDragEnd}
          >
            <Table ref={tableRef} className="w-full" id="ai-fields-table">
              <DraggableTableHeader
                table={table}
                columnOrder={columnOrder}
                handleSort={handleSort}
                sortConfig={{
                  key: filters.sortColumn || "",
                  direction: filters.sortDirection as SortDirection,
                }}
                renderSortIcon={renderSortIcon}
                filters={filters}
                updateFilter={updateFilter}
                clearFilter={clearFilter}
                columns={visibleColumns}
                handleDoubleClickResize={(_e, colId) =>
                  handleSingleColumnResize(colId)
                }
                pinnedColumns={pinnedColumns}
                onTogglePin={togglePin}
                onResizeToFit={handleSingleColumnResize}
                onRemoveColumn={handleRemoveColumn}
                columnAliases={columnAliases}
                onSetAlias={handleSetAlias}
                wrappedColumns={wrappedColumns}
                onToggleWrap={toggleWrap}
              />
              <TableBody className="bg-card">
                {isLoading ? (
                  // Loading skeleton
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      {visibleColumns.map((col) => (
                        <TableCell key={col.key as string}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : aiFields.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="text-center py-8"
                    >
                      {debouncedSearchQuery || hasFilters ? (
                        <div className="text-muted-foreground">
                          No AI fields found matching your criteria
                        </div>
                      ) : (
                        <div className="text-muted-foreground">
                          No AI fields have been created yet.
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="hover:bg-muted/50 transition-colors"
                    >
                      {row.getVisibleCells().map((cell) => {
                        const isPinned = pinnedColumns.includes(cell.column.id);
                        const isWrapped = wrappedColumns.includes(
                          cell.column.id,
                        );

                        return (
                          <TableCell
                            key={cell.id}
                            style={{
                              width: cell.column.getSize(),
                              position: isPinned ? "sticky" : "relative",
                              left: isPinned
                                ? cell.column.getStart()
                                : undefined,
                              zIndex: isPinned ? 10 : undefined,
                            }}
                            className={cn(
                              isPinned &&
                                "bg-card shadow-[2px_0_5px_-3px_rgba(0,0,0,0.1)]",
                              !isWrapped && "truncate",
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Infinite scroll sentinel */}
            <div ref={loadMoreRef} className="h-1" />

            {/* Loading more indicator */}
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Skeleton className="h-8 w-32" />
              </div>
            )}
          </DndContext>
        </div>
      </Card>

      {/* Modals */}
      <AIFieldEditModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        field={selectedField}
        onSuccess={handleModalClose}
      />

      {aliasModalColumn && (
        <ColumnAliasModal
          open={!!aliasModalColumn}
          onOpenChange={(open) => !open && setAliasModalColumn(null)}
          columnId={aliasModalColumn}
          originalTitle={
            visibleColumns.find((c) => (c.key as string) === aliasModalColumn)
              ?.title || ""
          }
          currentAlias={columnAliases[aliasModalColumn]}
          onSave={handleSaveAlias}
        />
      )}
    </>
  );
}
